import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { MdEvent, MdDashboard, MdBolt, MdSecurity, MdSatelliteAlt } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { crudDe } from "../../../services/acesso";
import { WHATSAPP_SUPORTE_URL } from '../../../services/constantes';
import { LocaSmartLayout } from '../../../components/locasmart-layout';
import Listagem from '../../../components/listagem';
import ModalForm from '../../../components/modal-form';
import { confirm } from '../../../components/confirm';
import { TipoFormulario } from '../../../services/constantes';
import { useAppSelector } from '../../../store/hooks';
import { obterIdTenent, usuarioTemPermissao } from '../../../services/seguranca';
import { AcaoGrid, EventoRisco, EventoRiscoAlvo } from '../../../services/tipos';
import api from '../../../services/api';
import {
  camposEvento,
  configInadimplenciaDefault,
  ALVO_POR_VEICULO,
  ALVO_POR_LOCATARIO,
  ALVO_POR_LOCACAO,
  ALVO_TODOS,
  TIPO_MONITORAMENTO_ENDERECO,
  TIPO_DISTANCIAMENTO,
  TIPO_ALERTA_BLOQUEIO_DISTANCIAMENTO,
  TIPO_INATIVIDADE,
  TIPO_EXCESSO_ATIVIDADE,
  REGRA_DIAS_INATIVOS,
  REGRA_KM_BAIXO,
} from './campos';
import { listarEventos, excluirEvento, pausarEvento, retomarEvento, verificarModulo, buscarEmpresa, geocodificarCidade } from '../api';
import LogTab from './log-tab';
import OcorrenciasTab from './ocorrencias-tab';
import ModalModelosRisco, { ModeloRisco } from '../components/modal-modelos-risco';

// ── Abas estilizadas (underline azul, padrão das telas de vistoria/Check) ──
const TabBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
`;

const TabButton = styled.button<{ $ativa: boolean }>`
  background: none;
  border: none;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: ${({ $ativa }) => ($ativa ? 600 : 400)};
  color: ${({ $ativa }) => ($ativa ? '#1E47A8' : '#555')};
  border-bottom: 2.5px solid ${({ $ativa }) => ($ativa ? '#1E47A8' : 'transparent')};
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: -1px;

  &:hover {
    color: #1E47A8;
    background: ${({ $ativa }) => ($ativa ? 'transparent' : '#f2f5fb')};
  }
`;

// Ícone de "painel/torre de controle" no menu de ações — pulso leve para dar vida (bem interativo).
const IconePainel = styled(MdDashboard)`
  animation: pulsoPainel 2.4s ease-in-out infinite;
  @keyframes pulsoPainel {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
`;

const Pill: React.FC<{ texto: string; cor: string }> = ({ texto, cor }) => (
  <span
    style={{
      backgroundColor: cor,
      color: 'white',
      padding: '5px 10px',
      borderRadius: '5px',
      fontWeight: 'bold',
      fontSize: 12,
      textAlign: 'center',
      whiteSpace: 'nowrap',
    }}
  >
    {texto}
  </span>
);

const BotaoWhatsApp = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 10px 20px;
  background: #25D366;
  color: #fff;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: #1eb457;
  }
`;

const PainelBloqueio: React.FC<{ icone: React.ReactNode; titulo: string; children: React.ReactNode; acao?: React.ReactNode }> = ({ icone, titulo, children, acao }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    textAlign: 'center', padding: '64px 24px', gap: 12,
  }}>
    <div style={{
      width: 72, height: 72, borderRadius: '50%', background: '#eef2f8',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1F467E',
    }}>
      {icone}
    </div>
    <h3 style={{ margin: 0, color: '#1F467E', fontSize: 20 }}>{titulo}</h3>
    <p style={{ margin: 0, color: '#5a6b7d', fontSize: 14.5, maxWidth: 520, lineHeight: 1.6 }}>{children}</p>
    {acao}
  </div>
);

const GestaoRiscoListagem: React.FC = () => {
  const token = useAppSelector(s => s.token);
  const idEmpresa = obterIdTenent(token);

  const [aba, setAba] = useState<'eventos' | 'ocorrencias' | 'log'>('eventos');
  // Gate do módulo: cobrado à parte (flag por empresa) e dependente do rastreamento integrado.
  const [moduloHabilitado, setModuloHabilitado] = useState<boolean | null>(null);
  const [integracaoOk, setIntegracaoOk] = useState<boolean | null>(null);
  const [filtroVeiculo, setFiltroVeiculo] = useState<{ idVeiculo: string; placa: string } | null>(null);
  const [eventos, setEventos] = useState<EventoRisco[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [exibirModal, setExibirModal] = useState(false);
  const [exibirMenuModelos, setExibirMenuModelos] = useState(false);
  const [empresa, setEmpresa] = useState<{ cidade?: string; estado?: string } | null>(null);
  const [modo, setModo] = useState(TipoFormulario.tpCadastro);
  const [selecionado, setSelecionado] = useState<any>(null);

  // Listas para os multiselects de alvo
  const [veiculos, setVeiculos] = useState<any[]>([]);
  const [locatarios, setLocatarios] = useState<any[]>([]);
  // Seleção corrente e inicial (para seed do SelectMulti ao abrir o modal)
  const [veiculosAlvoSel, setVeiculosAlvoSel] = useState<any[]>([]);
  const [locatariosAlvoSel, setLocatariosAlvoSel] = useState<any[]>([]);
  const [veiculosInicial, setVeiculosInicial] = useState<any[]>([]);
  const [locatariosInicial, setLocatariosInicial] = useState<any[]>([]);
  // Monitoramento de Endereço: ids das locações escolhidas.
  const [locacoesAlvoSel, setLocacoesAlvoSel] = useState<string[]>([]);
  // Seleção ao abrir o modal (seed da lista). Fica fora das dependências dos campos: a lista guarda a própria
  // seleção, e recriar os campos a cada clique desmontava a lista no meio da localização do endereço.
  const [locacoesInicial, setLocacoesInicial] = useState<string[]>([]);

  // Carrega locatários (todos) e veículos (APENAS os integrados à Agillock/rastreador,
  // mesmo critério do Plano de Manutenção — só faz sentido mirar veículo rastreável).
  useEffect(() => {
    api.get(`/LocatariosPorEmpresa/${idEmpresa}`)
      .then(r => setLocatarios(r.data || []))
      .catch(() => { });

    buscarEmpresa(idEmpresa)
      .then(e => setEmpresa(e || null))
      .catch(() => { });

    const carregarVeiculos = async () => {
      try {
        const [veiculosRes, configRes] = await Promise.all([
          api.get(`/VeiculoPorEmpresa/${idEmpresa}`),
          api.get(`/ConfiguracaoPorEmpresa/${idEmpresa}`).catch(() => ({ data: null })),
        ]);

        const possuiIntegracao = !!(configRes.data?.azulMonitorConfigurado);
        setIntegracaoOk(possuiIntegracao);
        if (!possuiIntegracao) {
          setVeiculos([]);
          return;
        }

        const placas = (veiculosRes.data || []).map((v: any) => v.placa).filter(Boolean);
        let placasRastreadas = new Set<string>();
        try {
          const statusRes = await api.post(`/AzulMonitor/VerificarPlacas/${idEmpresa}`, placas);
          placasRastreadas = new Set(
            (statusRes.data || []).filter((s: any) => s.rastreada).map((s: any) => s.placa)
          );
        } catch { /* rastreador não configurado */ }

        setVeiculos((veiculosRes.data || []).filter((v: any) => placasRastreadas.has(v.placa)));
      } catch {
        setVeiculos([]);
      }
    };
    carregarVeiculos();
  }, [idEmpresa]);

  // Achata configuracao (JSON) para os campos do form
  const paraForm = useCallback((e?: EventoRisco) => {
    if (!e) {
      return {
        tipo: 1,
        alvoTipo: 1,
        ativo: true,
        temporario: false,
        alertarMotorista: false,
        // Evento de risco sem avisar a locadora não serve para nada: nasce ligado.
        alertarLocador: true,
        diasAtrasoParaAcao: 5,
        desbloqueioAutomaticoAoPagar: true,
        regraInatividade: REGRA_DIAS_INATIVOS,
        criterioInatividade: 1,
        diasInativos: 7,
        janelaDias: 7,
        kmMinimo: 50,
        kmMaximo: 1000,
        horaInicio: '00:00',
        horaFim: '05:00',
        raioMetros: 300,
        noitesFora: 3,
        janelaNoites: 7,
      };
    }
    const cfg = e.configuracao ? JSON.parse(e.configuracao) : {};
    return {
      ...e,
      diasAtrasoParaAcao: cfg.diasAtrasoParaAcao ?? 5,
      desbloqueioAutomaticoAoPagar: cfg.desbloqueioAutomaticoAoPagar ?? true,
      regraInatividade: cfg.regra ?? REGRA_DIAS_INATIVOS,
      criterioInatividade: cfg.criterioInatividade ?? 1,
      diasInativos: cfg.diasInativos ?? 7,
      janelaDias: cfg.janelaDias ?? 7,
      kmMinimo: cfg.kmMinimo ?? 50,
      kmMaximo: cfg.kmMaximo ?? 1000,
      horaInicio: cfg.horaInicio ?? '00:00',
      horaFim: cfg.horaFim ?? '05:00',
      raioMetros: cfg.raioMetros ?? 300,
      noitesFora: cfg.noitesFora ?? 3,
      janelaNoites: cfg.janelaNoites ?? 7,
      cerca: (cfg.latRef || cfg.lngRef)
        ? {
            latRef: cfg.latRef,
            lngRef: cfg.lngRef,
            raioKm: cfg.raioKm ?? cfg.raioAlertaKm ?? 5,
            raioBloqueioKm: cfg.raioBloqueioKm,
          }
        : undefined,
    };
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setEventos(await listarEventos(idEmpresa));
    } catch {
      toast.error('Erro ao carregar eventos.');
    } finally {
      setCarregando(false);
    }
  }, [idEmpresa]);

  useEffect(() => {
    verificarModulo(idEmpresa)
      .then(setModuloHabilitado)
      .catch(() => setModuloHabilitado(true));
  }, [idEmpresa]);

  useEffect(() => {
    if (aba === 'eventos' && moduloHabilitado === true) carregar();
  }, [aba, carregar, moduloHabilitado]);

  const handleCriar = useCallback(() => {
    if (!usuarioTemPermissao(token, 'gestao_risco_novo')) {
      toast.error('Sem permissão.');
      return;
    }
    setExibirMenuModelos(true);
  }, [token]);

  // Abre o formulário de cadastro com os campos pré-preenchidos pelo modelo escolhido.
  const abrirFormComModelo = useCallback((overrides: any) => {
    setModo(TipoFormulario.tpCadastro);
    setSelecionado({ ...paraForm(), ...overrides });
    setVeiculosAlvoSel([]);
    setLocatariosAlvoSel([]);
    setVeiculosInicial([]);
    setLocatariosInicial([]);
    setLocacoesAlvoSel([]);
    setLocacoesInicial([]);
    setExibirModal(true);
  }, [paraForm]);

  const aplicarModelo = useCallback(async (modelo: ModeloRisco) => {
    setExibirMenuModelos(false);

    if (modelo === 'sublocacao') {
      abrirFormComModelo({
        nome: 'Sublocação',
        tipo: TIPO_EXCESSO_ATIVIDADE,
        janelaDias: 7,
        kmMaximo: 2240,
      });
      return;
    }

    if (modelo === 'poucoUso') {
      abrirFormComModelo({
        nome: 'Pouco Uso',
        tipo: TIPO_INATIVIDADE,
        regraInatividade: REGRA_KM_BAIXO,
        janelaDias: 7,
        kmMinimo: 140,
      });
      return;
    }

    if (modelo === 'pernoite') {
      abrirFormComModelo({
        nome: 'Pernoite fora do endereço',
        tipo: TIPO_MONITORAMENTO_ENDERECO,
        alvoTipo: ALVO_TODOS,
      });
      return;
    }

    if (modelo === 'fuga') {
      const coords = await geocodificarCidade(empresa);
      if (!coords) {
        toast.info('Não localizamos a cidade da empresa no mapa. Marque o ponto de origem manualmente.');
      }
      abrirFormComModelo({
        nome: 'Fuga da Região',
        tipo: TIPO_ALERTA_BLOQUEIO_DISTANCIAMENTO,
        cerca: {
          latRef: coords?.lat ?? 0,
          lngRef: coords?.lng ?? 0,
          raioKm: 30,
          raioBloqueioKm: 40,
        },
      });
      return;
    }

    // Personalizado: formulário em branco.
    abrirFormComModelo({});
  }, [abrirFormComModelo, empresa]);

  const abrirModalCom = useCallback((e: EventoRisco, modoAbertura: TipoFormulario) => {
    setModo(modoAbertura);
    setSelecionado(paraForm(e));

    // Resolve os alvos salvos para os objetos completos (seed dos multiselects)
    const alvos = e.alvos || [];
    const veicIniciais = alvos
      .filter(a => a.idVeiculo)
      .map(a => veiculos.find(v => v.id === a.idVeiculo))
      .filter(Boolean);
    const locIniciais = alvos
      .filter(a => a.idLocatario)
      .map(a => locatarios.find(l => l.id === a.idLocatario))
      .filter(Boolean);

    setVeiculosInicial(veicIniciais);
    setLocatariosInicial(locIniciais);
    setVeiculosAlvoSel(veicIniciais);
    setLocatariosAlvoSel(locIniciais);
    const locacoesSalvas = alvos.filter(a => a.idLocacao).map(a => a.idLocacao as string);
    setLocacoesAlvoSel(locacoesSalvas);
    setLocacoesInicial(locacoesSalvas);
    setExibirModal(true);
  }, [paraForm, veiculos, locatarios]);

  const handleEditar = useCallback((e: EventoRisco) => abrirModalCom(e, TipoFormulario.tpEdicao), [abrirModalCom]);

  const handleVisualizar = useCallback((e: EventoRisco) => abrirModalCom(e, TipoFormulario.tpVisualizacao), [abrirModalCom]);

  const handleExcluir = useCallback(async (e: EventoRisco) => {
    try {
      await excluirEvento(e.id!, idEmpresa);
      toast.success('Excluído.');
      carregar();
    } catch {
      toast.error('Erro ao excluir.');
    }
  }, [idEmpresa, carregar]);

  const handlePausar = useCallback(async (e: EventoRisco) => {
    try {
      if (e.pausadoGlobal) {
        await retomarEvento(e.id!, idEmpresa, true);
        toast.success('Retomado.');
      } else {
        await pausarEvento(e.id!, idEmpresa, true);
        toast.success('Pausado.');
      }
      carregar();
    } catch {
      toast.error('Erro ao pausar/retomar.');
    }
  }, [idEmpresa, carregar]);

  // onSave substitui o auto-submit do ModalForm:
  // monta o payload DTO (config JSON + alvos conforme alvoTipo + expiração)
  // e executa a dupla confirmação antes de salvar quando ativando bloqueio.
  const onSave = useCallback(async (entidade: any) => {
    const alvoTipo = Number(entidade.alvoTipo);
    const ehPernoite = Number(entidade.tipo) === TIPO_MONITORAMENTO_ENDERECO;

    // Monta os alvos a partir da seleção corrente
    let alvos: EventoRiscoAlvo[] = [];
    if (ehPernoite) {
      if (alvoTipo !== ALVO_TODOS && alvoTipo !== ALVO_POR_LOCACAO) {
        toast.error('Em "Aplicar a", escolha todas as locações ativas ou locações escolhidas.');
        return;
      }
      if (alvoTipo === ALVO_POR_LOCACAO) {
        alvos = locacoesAlvoSel.map(id => ({ idLocacao: id, pausado: false }));
        if (alvos.length === 0) {
          toast.error('Escolha ao menos uma locação.');
          return;
        }
      }
    } else if (alvoTipo === ALVO_POR_LOCACAO) {
      toast.error('A aplicação por locação existe só no Monitoramento de Endereço.');
      return;
    } else if (alvoTipo === ALVO_POR_VEICULO) {
      alvos = veiculosAlvoSel.map(v => ({ idVeiculo: v.id, pausado: false }));
      if (alvos.length === 0) {
        toast.error('Selecione ao menos um veículo alvo.');
        return;
      }
    } else if (alvoTipo === ALVO_POR_LOCATARIO) {
      alvos = locatariosAlvoSel.map(l => ({ idLocatario: l.id, pausado: false }));
      if (alvos.length === 0) {
        toast.error('Selecione ao menos um locatário alvo.');
        return;
      }
    }

    const temporario = Boolean(entidade.temporario);
    if (temporario && !entidade.dataExpiracao) {
      toast.error('Informe a data de expiração ou desmarque "Esse evento deverá expirar".');
      return;
    }

    const tipoNum = Number(entidade.tipo);
    // Tipos que só ALERTAM (sem bloqueio): com os dois alertas desligados o evento detecta e não avisa ninguém.
    const soAlerta = tipoNum === TIPO_DISTANCIAMENTO || tipoNum === TIPO_INATIVIDADE || tipoNum === TIPO_EXCESSO_ATIVIDADE
      || tipoNum === TIPO_MONITORAMENTO_ENDERECO;
    if (soAlerta && !entidade.alertarLocador && !entidade.alertarMotorista) {
      toast.error('Marque "Alertar locador" ou "Alertar motorista": este tipo de evento só gera alerta, e sem isso ninguém é avisado.');
      return;
    }
    const ehDistanciamento = tipoNum === TIPO_DISTANCIAMENTO;
    const ehAlertaBloqueio = tipoNum === TIPO_ALERTA_BLOQUEIO_DISTANCIAMENTO;
    const ehInatividade = tipoNum === TIPO_INATIVIDADE;
    const ehExcesso = tipoNum === TIPO_EXCESSO_ATIVIDADE;
    const ativandoBloqueio = Boolean(entidade.ativo) && (tipoNum === 1 || ehAlertaBloqueio);

    if (ehPernoite) {
      const raio = Number(entidade.raioMetros);
      const fora = Number(entidade.noitesFora);
      const janela = Number(entidade.janelaNoites);
      const hora = /^([01]\d|2[0-3]):[0-5]\d$/;
      if (!hora.test(entidade.horaInicio || '') || !hora.test(entidade.horaFim || '')) {
        toast.error('Informe o horário da madrugada (início e fim).');
        return;
      }
      if (!(raio >= 100 && raio <= 2000)) {
        toast.error('O raio de tolerância deve ficar entre 100 m e 2.000 m.');
        return;
      }
      if (!(janela >= 3 && janela <= 30)) {
        toast.error('A janela deve ter entre 3 e 30 noites.');
        return;
      }
      if (!(fora >= 1 && fora <= janela)) {
        toast.error('As noites fora devem ficar entre 1 e o total de noites da janela.');
        return;
      }
    }

    if (ehExcesso && !(Number(entidade.janelaDias) > 0 && Number(entidade.kmMaximo) > 0)) {
      toast.error('Informe a janela em dias e o km máximo.');
      return;
    }

    if (ehInatividade) {
      const regra = Number(entidade.regraInatividade);
      if (regra === REGRA_DIAS_INATIVOS && !(Number(entidade.diasInativos) > 0)) {
        toast.error('Informe quantos dias inativos disparam o alerta.');
        return;
      }
      if (regra === REGRA_KM_BAIXO && !(Number(entidade.janelaDias) > 0 && Number(entidade.kmMinimo) > 0)) {
        toast.error('Informe a janela em dias e o km mínimo.');
        return;
      }
    }

    if (ehDistanciamento || ehAlertaBloqueio) {
      const c = entidade.cerca;
      if (!c || (!c.latRef && !c.lngRef) || !(c.raioKm > 0)) {
        toast.error('Defina o ponto no mapa e um raio maior que zero.');
        return;
      }
      if (ehAlertaBloqueio && !(c.raioBloqueioKm > 0 && c.raioBloqueioKm >= c.raioKm)) {
        toast.error('O raio de bloqueio deve ser maior ou igual ao raio de alerta.');
        return;
      }
    }

    if (ativandoBloqueio) {
      const c1 = await confirm(
        'Este evento pode BLOQUEAR remotamente veículos de clientes. Deseja ativar?'
      );
      if (!c1) return;

      const c2 = await confirm(
        'Confirme novamente: ativar o bloqueio automático de veículos?',
        { alert: true, waitSeconds: 5, sound: true }
      );
      if (!c2) return;
    }

    try {
      let configuracao: string;
      if (ehPernoite) {
        configuracao = JSON.stringify({
          horaInicio: entidade.horaInicio,
          horaFim: entidade.horaFim,
          raioMetros: Number(entidade.raioMetros),
          noitesFora: Number(entidade.noitesFora),
          janelaNoites: Number(entidade.janelaNoites),
        });
      } else if (ehDistanciamento) {
        const c = entidade.cerca;
        configuracao = JSON.stringify({ latRef: c.latRef, lngRef: c.lngRef, raioKm: c.raioKm });
      } else if (ehAlertaBloqueio) {
        const c = entidade.cerca;
        configuracao = JSON.stringify({
          latRef: c.latRef,
          lngRef: c.lngRef,
          raioAlertaKm: c.raioKm,
          raioBloqueioKm: c.raioBloqueioKm,
        });
      } else if (ehExcesso) {
        configuracao = JSON.stringify({
          janelaDias: Number(entidade.janelaDias),
          kmMaximo: Number(entidade.kmMaximo),
        });
      } else if (ehInatividade) {
        const regra = Number(entidade.regraInatividade);
        configuracao = regra === REGRA_KM_BAIXO
          ? JSON.stringify({
              regra,
              janelaDias: Number(entidade.janelaDias),
              kmMinimo: Number(entidade.kmMinimo),
            })
          : JSON.stringify({
              regra: REGRA_DIAS_INATIVOS,
              criterioInatividade: Number(entidade.criterioInatividade) || 1,
              diasInativos: Number(entidade.diasInativos),
            });
      } else {
        const configBase = selecionado?.configuracao
          ? JSON.parse(selecionado.configuracao)
          : JSON.parse(configInadimplenciaDefault);
        configBase.diasAtrasoParaAcao = Number(entidade.diasAtrasoParaAcao ?? 5);
        configBase.desbloqueioAutomaticoAoPagar = entidade.desbloqueioAutomaticoAoPagar !== false;
        configuracao = JSON.stringify(configBase);
      }

      const payload: EventoRisco = {
        idEmpresa,
        nome: entidade.nome,
        tipo: Number(entidade.tipo),
        ativo: Boolean(entidade.ativo),
        pausadoGlobal: Boolean(entidade.pausadoGlobal ?? false),
        alvoTipo,
        temporario,
        dataExpiracao: temporario ? entidade.dataExpiracao : undefined,
        alertarMotorista: Boolean(entidade.alertarMotorista ?? false),
        alertarLocador: Boolean(entidade.alertarLocador ?? false),
        canaisMotorista: entidade.alertarMotorista ? 7 : 0,
        canaisLocador: entidade.alertarLocador ? 7 : 0,
        configuracao,
        alvos,
      };

      if (modo === TipoFormulario.tpEdicao) {
        await api.put(`/EventoRisco/${entidade.id}`, { ...payload, id: entidade.id });
        toast.success('Evento atualizado.');
      } else {
        await api.post('/EventoRisco/', payload);
        toast.success('Evento criado.');
      }

      setExibirModal(false);
      carregar();
    } catch (err: any) {
      const msg = err?.response?.data?.Mensagem || err?.response?.data?.mensagem || 'Erro ao salvar evento.';
      toast.error(msg);
    }
  }, [idEmpresa, modo, selecionado, carregar, veiculosAlvoSel, locatariosAlvoSel, locacoesAlvoSel]);

  const campos = useMemo(
    () => camposEvento({
      modo,
      veiculosDataSource: veiculos,
      locatariosDataSource: locatarios,
      veiculosInicial,
      locatariosInicial,
      onVeiculosChange: setVeiculosAlvoSel,
      onLocatariosChange: setLocatariosAlvoSel,
      idEmpresa,
      locacoesIniciais: locacoesInicial,
      onLocacoesChange: setLocacoesAlvoSel,
    }),
    [modo, veiculos, locatarios, veiculosInicial, locatariosInicial, idEmpresa, locacoesInicial]
  );

  const acoesExtras: AcaoGrid[] = [
    {
      Texto: 'Pausar/Retomar',
      OnClick: handlePausar,
    },
  ];

  const colunas = [
    {
      field: 'nome',
      headerName: 'Evento',
      flex: 1,
    },
    {
      field: 'ativo',
      headerName: 'Ativo',
      width: 110,
      renderCell: (p: any) =>
        p.value
          ? <Pill texto="ATIVO" cor="#4CAF50" />
          : <Pill texto="INATIVO" cor="#9e9e9e" />,
    },
    {
      field: 'pausadoGlobal',
      headerName: 'Pausado',
      width: 140,
      renderCell: (p: any) => {
        if (!p.row.ativo) return <Pill texto="PARADO" cor="#9e9e9e" />;
        return p.value
          ? <Pill texto="PAUSADO" cor="#FFC107" />
          : <Pill texto="EM EXECUÇÃO" cor="#2196F3" />;
      },
    },
    {
      field: 'execucoes',
      headerName: 'Execuções',
      width: 120,
      renderCell: (p: any) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#1F467E' }}>
          <MdBolt size={16} /> {p.value ?? 0}
        </span>
      ),
    },
  ];

  if (moduloHabilitado === false) {
    return (
      <LocaSmartLayout>
        <PainelBloqueio
          icone={<MdSecurity size={34} />}
          titulo="Gestão de Risco"
          acao={(
            <BotaoWhatsApp
              href={`${WHATSAPP_SUPORTE_URL}?text=${encodeURIComponent('Olá! Tenho interesse em contratar o módulo de Gestão de Risco do LocaSmartPro.')}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaWhatsapp size={18} /> Falar com o comercial
            </BotaoWhatsApp>
          )}
        >
          A Gestão de Risco é um módulo adicional do LocaSmartPro, com eventos automáticos de
          bloqueio e alerta sobre a frota rastreada. Ele não está habilitado para a sua conta.
          Fale com o nosso time comercial para contratar.
        </PainelBloqueio>
      </LocaSmartLayout>
    );
  }

  if (integracaoOk === false) {
    return (
      <LocaSmartLayout>
        <PainelBloqueio icone={<MdSatelliteAlt size={34} />} titulo="Rastreamento não configurado">
          A Gestão de Risco depende da integração com o rastreador, assim como o Mapa de Veículos.
          Configure o rastreamento em Minha Conta, aba Integração, e volte aqui para criar os eventos.
        </PainelBloqueio>
      </LocaSmartLayout>
    );
  }

  return (
    <LocaSmartLayout>
      <TabBar>
        <TabButton $ativa={aba === 'eventos'} onClick={() => setAba('eventos')}>
          Eventos
        </TabButton>
        <TabButton $ativa={aba === 'ocorrencias'} onClick={() => setAba('ocorrencias')}>
          Ocorrências ao vivo
        </TabButton>
        <TabButton $ativa={aba === 'log'} onClick={() => setAba('log')}>
          Log
        </TabButton>
      </TabBar>

      {aba === 'eventos' && (
        <>
          <Listagem
              permissoes={crudDe('gestao_risco')}
            carregando={carregando}
            linhas={eventos}
            colunas={colunas}
            nomeEntidade="Evento de Risco"
            nomePropriedade="nome"
            acoesExtras={acoesExtras}
            exibirAcoesDeCrud={true}
            handleCriar={handleCriar}
            handleEditar={handleEditar}
            handleVisualizacao={handleVisualizar}
            handleExclusao={handleExcluir}
            handleSelecionarEntidade={setSelecionado}
            IconeEntidade={MdEvent}
            iconeAcoes={IconePainel as any}
          />

          <ModalForm
            show={exibirModal}
            modo={modo}
            tamanho="xl"
            campos={campos}
            carregando={carregando}
            entidadeSelecionada={selecionado}
            onHide={() => { setExibirModal(false); carregar(); }}
            onCancel={() => setExibirModal(false)}
            onSave={onSave}
            apiConfig={{
              endpoint: 'EventoRisco',
              idTenentCampo: 'idEmpresa',
              requisicaoBemSucedida: () => { },
              tratarErro: () => toast.error('Erro ao salvar evento.'),
            }}
          />

          <ModalModelosRisco
            show={exibirMenuModelos}
            onClose={() => setExibirMenuModelos(false)}
            onEscolher={aplicarModelo}
          />
        </>
      )}

      {aba === 'ocorrencias' && (
        <OcorrenciasTab
          idEmpresa={idEmpresa}
          onVerHistorico={(idVeiculo, placa) => { setFiltroVeiculo({ idVeiculo, placa }); setAba('log'); }}
        />
      )}

      {aba === 'log' && (
        <LogTab idEmpresa={idEmpresa} filtroVeiculo={filtroVeiculo} onLimparFiltroVeiculo={() => setFiltroVeiculo(null)} />
      )}
    </LocaSmartLayout>
  );
};

export default GestaoRiscoListagem;
